import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { ArrowRight, Trophy, CreditCard, Users, CalendarDays, Share2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl, shareLink } from "../lib/api";
import { AdCarousel, ProductShowcase } from "../components/Carousels";
import { InstallButton } from "../components/InstallButton";
import { Loader, Logo } from "../components/common";

const HERO = "https://images.unsplash.com/photo-1686753767715-37cb0c34212c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const login = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const SponsorCard = ({ s }) => {
  const inner = (
    <div className="card-x card-hover flex h-28 w-56 shrink-0 items-center justify-center overflow-hidden p-3">
      {s.image_path ? <img src={fileUrl(s.image_path)} alt={s.name} className="max-h-full max-w-full object-contain" /> :
        <span className="h-display text-2xl text-white/80">{s.name}</span>}
    </div>
  );
  return s.link ? <a href={s.link} target="_blank" rel="noreferrer" data-testid={`sponsor-${s.id}`}>{inner}</a> : <div data-testid={`sponsor-${s.id}`}>{inner}</div>;
};

const Sponsors = ({ sponsors }) => {
  if (!sponsors.length) {
    return (
      <div data-testid="sponsors-empty" className="card-x flex h-28 items-center justify-center border-dashed text-sm text-[#64748B]">
        Espaço reservado para patrocinadores
      </div>
    );
  }
  const loop = sponsors.length > 2 ? [...sponsors, ...sponsors] : sponsors;
  return (
    <div className="overflow-hidden">
      <div className={`flex gap-4 ${sponsors.length > 2 ? "marquee w-max" : ""}`}>
        {loop.map((s, i) => <SponsorCard key={`${s.id}-${i}`} s={s} />)}
      </div>
    </div>
  );
};

export default function Landing() {
  const { user, loading, settings } = useAuth();
  const [sponsors, setSponsors] = useState([]);
  useEffect(() => { api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => {}); }, []);
  if (loading) return <Loader full />;
  if (user && user.status === "approved") return <Navigate to="/app" replace />;
  if (user) return <Navigate to="/aguardando" replace />;

  const share = async () => {
    const r = await shareLink(window.location.origin, settings?.app_name || "Circuito Altax");
    if (r === "copied") toast.success("Link copiado! Cole onde quiser compartilhar.");
    if (r === "fail") toast.error("Não foi possível compartilhar");
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0B0E14]">
      <div className="absolute inset-0">
        {settings?.login_video_path ? (
          <video data-testid="login-bg-video" key={settings.login_video_path} src={fileUrl(settings.login_video_path)} autoPlay muted loop playsInline className="h-full w-full object-cover opacity-40" />
        ) : <img src={HERO} alt="" className="h-full w-full object-cover opacity-25" />}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14]/50 via-[#0B0E14]/80 to-[#0B0E14]" />
      </div>
      <div className="absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-[#E4F222]/20 blur-[120px]" />
      <div className="relative mx-auto flex min-h-screen max-w-6xl flex-col px-5 py-8 sm:px-8">
        <header className="flex items-center justify-between gap-4 rise">
          <div className="min-w-0 shrink"><Logo where="login" /></div>
          <div className="flex shrink-0 items-center gap-2 sm:gap-4">
            <InstallButton className="btn-ghost flex items-center gap-1.5 bg-[#0B0E14]/60 px-3" label={<span className="hidden sm:inline">Instalar</span>} />
            <button data-testid="share-page-btn" onClick={share} className="btn-ghost flex items-center gap-1.5 bg-[#0B0E14]/60 px-3"><Share2 className="h-4 w-4" /><span className="hidden sm:inline">Compartilhar</span></button>
            <button data-testid="header-login-btn" onClick={login} className="hidden text-sm font-semibold text-[#94A3B8] hover:text-white sm:block">Entrar</button>
          </div>
        </header>

        <section className="grid flex-1 items-center gap-12 py-10 lg:grid-cols-[1.2fr_1fr]">
          <div className="min-w-0">
            <div className="eyebrow rise rise-1">{settings?.app_name || "Circuito Altax de Futevôlei"}</div>
            <h1 className="h-display mt-3 text-5xl leading-[0.9] sm:text-6xl lg:text-7xl rise rise-2">
              {settings?.login_title ?? "Tudo em um"}<br /><span className="text-[#E4F222]">{settings?.login_highlight ?? "só lugar"}</span>
            </h1>
            <p className="mt-5 max-w-md text-base text-[#94A3B8] rise rise-3">{settings?.welcome_text}</p>
            <div className="mt-8 flex flex-wrap items-center gap-4 rise rise-4">
              <button data-testid="google-login-btn" onClick={login} className="btn-lime flex items-center gap-3 rounded-full px-7 py-4 text-base lime-glow">
                <svg className="h-5 w-5" viewBox="0 0 48 48"><path fill="#000" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z" /></svg>
                {settings?.login_button || "Entrar com Google"} <ArrowRight className="h-4 w-4" />
              </button>
              <span className="text-xs text-[#64748B]">{settings?.login_note}</span>
            </div>
            <div className="mt-8 rise rise-4"><AdCarousel placement="login" title={settings?.login_ads_title} /></div>
            <div className="mt-8 rise rise-4" data-testid="sponsors-section">
              <div className="eyebrow mb-3">{settings?.login_sponsors_title}</div>
              <Sponsors sponsors={sponsors} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 rise rise-3">
            {[CalendarDays, Trophy, CreditCard, Users].map((I, k) => {
              const ft = settings?.login_features?.[k];
              if (!ft || (!ft.title && !ft.desc)) return null;
              return (
                <div key={k} className="card-x p-5" data-testid={`login-feature-${k}`}>
                  <I className="h-8 w-8 text-[#E4F222]" strokeWidth={1.5} />
                  <div className="h-display mt-4 text-2xl">{ft.title}</div>
                  <div className="text-xs text-[#94A3B8]">{ft.desc}</div>
                </div>
              );
            })}
          </div>
        </section>
        <div className="pb-8 rise rise-4"><ProductShowcase onBuy={login} /></div>
      </div>
    </div>
  );
}
